import { useCallback, useMemo } from "react";

import { ConfigProvider, Empty } from "antd";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import { useNavigate } from "react-router-dom";

import {
    usePageConfig,
    RVPaddedPage,
    useQueryParamsV6,
    OperatorType,
    QueryParams,
    RVLoadingGrid as LoadingGrid,
} from "@regal-voice/ui";
import { SettingsPaths } from "Pages/settings/paths";
import { SETTINGS_NAV_TITLES } from "Pages/settings/settingsMetadata";

import { EventPageHeader } from "../../Components/EventPageHeader";
import { useEventData } from "../../Hooks/useEventData";
import { getColumns } from "./GridColumns";

const defaultQueryParams: Partial<QueryParams> = {
    orderBy: [
        {
            key: "eventName",
            value: "ASC",
        },
    ],
    filterBy: [
        {
            key: "showInFeed",
            value: ["true", "false"],
        },
    ],
    page: 1,
    pageSize: 25,
};

export default function EventDefinitionsGridPage(): JSX.Element {
    usePageConfig({ pageTitle: SETTINGS_NAV_TITLES[SettingsPaths.eventManagement] });

    const { queryParams, setUiQueryParams } = useQueryParamsV6(defaultQueryParams);
    const navigate = useNavigate();
    const { loading, events } = useEventData();

    const searchTerm = useMemo(
        () => (queryParams.filterBy ?? []).find((filter) => filter.key === "eventName")?.value as string,
        [queryParams]
    );
    const showInFeedFilter = useMemo(
        () => (queryParams.filterBy ?? []).find((filter) => filter.key === "showInFeed"),
        [queryParams]
    );

    const setSearchTerm = useCallback(
        (value: string) => {
            setUiQueryParams((prev) => ({
                ...prev,
                filterBy: [
                    ...(prev.filterBy ?? []).filter((filter) => filter.key !== "eventName"),
                    ...(!!value.length
                        ? [
                              {
                                  key: "eventName",
                                  value,
                                  operator: OperatorType.CONTAINS,
                              },
                          ]
                        : []),
                ],
            }));
        },
        [setUiQueryParams]
    );

    const filteredEvents = useMemo(() => {
        return orderBy(
            events
                .filter(
                    (event) =>
                        !searchTerm ||
                        searchTerm.trim().length === 0 ||
                        event.eventName.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .filter((event) => {
                    if (!showInFeedFilter) {
                        return true;
                    }
                    return (showInFeedFilter.value as Array<string>).includes(
                        get(event, showInFeedFilter.key).toString()
                    );
                }),
            queryParams.orderBy?.length ? [(event) => event.eventName.toLowerCase()] : [],
            queryParams.orderBy?.find((order) => order.key === "eventName")?.value.toString() === "DESC"
                ? ["desc"]
                : ["asc"]
        );
    }, [events, queryParams, searchTerm, showInFeedFilter]);

    return (
        <RVPaddedPage>
            <ConfigProvider
                renderEmpty={() => (
                    <>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </>
                )}
            >
                <LoadingGrid
                    rowKey="id"
                    tableSize="small"
                    loading={loading}
                    skeletonRowLines={2}
                    columns={getColumns(navigate, queryParams)}
                    columnsData={filteredEvents}
                    totalRecords={filteredEvents?.length}
                    pageSizeOptions={["25", "50", "100"]}
                    defaultParams={{ pageSize: queryParams.pageSize }}
                    pageWidth="wide"
                    flexibleHeader={<EventPageHeader filterValue={searchTerm} setFilterValue={setSearchTerm} />}
                />
            </ConfigProvider>
        </RVPaddedPage>
    );
}
